import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { timelineActionTypes } from '@store/Timeline/Timeline.actionTypes';
import { useMemberCompanyTimezones } from '@hooks/useMemberCompanyTimezones';
import { useMembersOptionsForSelect } from '@/hooks/useMembersOptionsForSelect';
import {
  useUrlDateRangeSync,
  useUrlMemberHydrationSync,
  useUrlMemberNavSync,
  useUrlTimezoneIgnoreFlagSync,
  useUrlFilterWrite,
  getDefaultMemberFromOptions,
} from '@/hooks/useUrlFilterSync';
import {
  getInitialReportDateRangeFromSearch,
  getReportFiltersFromSearch,
} from '@/utils/reportFiltersUrl';
import { UrlTrackingView } from './UrlTracking.view';

export const UrlTrackingComponent = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const { sessionsByDay, loading, filters } = useSelector(
    (state) => state.timeline
  );
  const [selectedMember, setSelectedMember] = useState(null);

  const team = useSelector((state) => state.team);
  const auth = useSelector((state) => state.auth);
  const teamId = team?.active?.activeTeamid;
  const companyId = auth?.profile?.activeCompany?.companyId;
  const memberId = selectedMember?.value || null;
  const {
    membersOptions,
    isLoading: membersLoading,
    isError: membersError,
    error: membersErrorObj,
    refetch: refetchMembers,
  } = useMembersOptionsForSelect(teamId);

  const membersOptionsRef = useRef(membersOptions);
  membersOptionsRef.current = membersOptions;

  const [range, setRange] = useState(() =>
    getInitialReportDateRangeFromSearch(location.search)
  );

  const [ignoreUrlTimezone, setIgnoreUrlTimezone] = useState(false);
  const userOverrideTimezoneRef = useRef(false);

  useUrlDateRangeSync({ location, setDateRange: setRange });
  useUrlMemberHydrationSync({
    location,
    enabled: Boolean(teamId),
    membersOptions,
    selectedMember,
    setSelectedMember,
    currentUserId: auth?.profile?.id,
    getDefaultMember: getDefaultMemberFromOptions,
  });
  useUrlMemberNavSync({
    location,
    enabled: Boolean(teamId),
    membersOptionsRef,
    setSelectedMember,
  });
  useUrlTimezoneIgnoreFlagSync({ history, setIgnoreUrlTimezone });

  const filtersFromUrl = useMemo(
    () => getReportFiltersFromSearch(location.search),
    [location.search]
  );
  const urlTimezoneForHook = useMemo(
    () => (!ignoreUrlTimezone && filtersFromUrl.tz ? filtersFromUrl.tz : null),
    [ignoreUrlTimezone, filtersFromUrl.tz]
  );

  const {
    timezoneOptions,
    selectedTimezone,
    setSelectedTimezone,
    timezonesLoading,
  } = useMemberCompanyTimezones({
    teamId,
    memberId,
    companyId,
    initialTimezone: urlTimezoneForHook || filters?.timeZone || null,
    autoSelectMemberTimezone: true,
    userOverrideTimezoneRef,
  });

  const writeParams = useMemo(() => {
    const params = { start: range.start, end: range.end };
    if (selectedMember?.value) params.member = selectedMember.value;
    if (selectedTimezone) params.tz = selectedTimezone;
    return params;
  }, [range.start, range.end, selectedMember?.value, selectedTimezone]);

  useUrlFilterWrite({
    history,
    location,
    params: writeParams,
    enabled: Boolean(teamId && range?.start && range?.end),
  });

  const onMemberFilterChange = useCallback(
    (opt) => {
      userOverrideTimezoneRef.current = false;
      setIgnoreUrlTimezone(true);
      setSelectedMember(opt);
    },
    [setSelectedMember]
  );

  const handleTimezoneFilterChange = useCallback(
    (value) => {
      userOverrideTimezoneRef.current = true;
      setIgnoreUrlTimezone(true);
      setSelectedTimezone(value);
    },
    [setSelectedTimezone, setIgnoreUrlTimezone, userOverrideTimezoneRef]
  );

  const getEmployeeIdForUser = (userId) => {
    if (!userId) return '';
    const match = membersOptions.find((m) => m.value === userId);
    if (match?.employeeId) return match.employeeId;
    try {
      const stored = JSON.parse(localStorage.getItem('members')) || [];
      const found = stored.find((m) => m.userUID === userId);
      if (found?.employeeId) return found.employeeId;
    } catch (e) {
      console.warn('Unable to parse members from localStorage', e);
    }
    return userId;
  };

  const getRateForUser = (userId) => {
    const match = membersOptions.find((m) => m.value === userId);
    return match?.rate || 0;
  };

  const fetchTimeline = () => {
    if (!selectedMember) return;
    const employeeId = getEmployeeIdForUser(selectedMember.value);
    dispatch({
      type: timelineActionTypes.GET_SESSIONS,
      payload: {
        teamId,
        userUID: selectedMember.value,
        companyId,
        employeeId,
        memberId: selectedMember.value,
        startDate: range.start,
        endDate: range.end,
        timeZone: selectedTimezone,
        page: 1,
        pageSize: 1000, // Large page size to get all sessions
        rate: getRateForUser(selectedMember.value),
      },
    });
  };

  useEffect(() => {
    if (selectedMember) {
      fetchTimeline();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMember, selectedTimezone, range.start, range.end]);

  // A single date is selected, so at most one day bucket comes back.
  const day = sessionsByDay?.[0];

  return (
    <UrlTrackingView
      membersOptions={membersOptions}
      selectedMember={selectedMember}
      onMemberFilterChange={onMemberFilterChange}
      membersLoading={membersLoading}
      membersError={membersError}
      membersErrorObj={membersErrorObj}
      refetchMembers={refetchMembers}
      timezoneOptions={timezoneOptions}
      selectedTimezone={selectedTimezone}
      onTimezoneChange={handleTimezoneFilterChange}
      timezonesLoading={timezonesLoading}
      date={range.start}
      onDateChange={(dateStr) => setRange({ start: dateStr, end: dateStr })}
      onRefresh={fetchTimeline}
      loading={loading}
      day={day}
      teamId={teamId}
    />
  );
};
